import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { ui, useLang } from "../i18n/i18n";

export default function Contact() {
  const { t } = useLang();

  const items = [
    {
      icon: Mail,
      label: t(ui.contact.items.email),
      value: "Info@azroil.az",
      href: "mailto:Info@azroil.az",
      idx: "01",
      testid: "contact-email",
    },
    {
      icon: Phone,
      label: t(ui.contact.items.phone),
      value: "+994 55 254 34 44",
      href: "tel:+994552543444",
      idx: "02",
      testid: "contact-phone",
    },
    {
      icon: MapPin,
      label: t(ui.contact.items.office),
      value: t(ui.contact.items.officeValue),
      idx: "03",
      testid: "contact-office",
    },
    {
      icon: Clock,
      label: t(ui.contact.items.hours),
      value: t(ui.contact.items.hoursValue),
      idx: "04",
      testid: "contact-hours",
    },
  ];

  return (
    <div data-testid="contact-page">
      <section className="border-b border-[#E5E7EB]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-8">
              <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                {t(ui.contact.crumb)}
              </div>
              <div className="mt-4 w-8 h-px bg-[#0B1F3A]" />
              <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0B1F3A] leading-[1.04]">
                {t(ui.contact.h1)}
              </h1>
              <p className="mt-8 text-[#4B5563] text-[17px] md:text-[19px] max-w-2xl leading-relaxed">
                {t(ui.contact.lead)}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-[#E5E7EB]" data-testid="contact-grid">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {items.map(({ icon: Icon, label, value, href, idx, testid }) => {
              const content = (
                <>
                  <div className="flex items-start justify-between">
                    <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                      {idx}
                    </div>
                    <Icon size={18} className="text-[#0B1F3A]" />
                  </div>
                  <div className="mt-10">
                    <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#6B7280]">
                      {label}
                    </div>
                    <div className="mt-2 text-2xl md:text-3xl font-semibold text-[#0B1F3A] tracking-tight">
                      {value}
                    </div>
                  </div>
                </>
              );
              return href ? (
                <a
                  key={label}
                  href={href}
                  data-testid={testid}
                  className="bg-white p-8 md:p-12 min-h-[240px] flex flex-col justify-between hover:bg-[#F7F8FA] transition-colors"
                >
                  {content}
                </a>
              ) : (
                <div
                  key={label}
                  data-testid={testid}
                  className="bg-white p-8 md:p-12 min-h-[240px] flex flex-col justify-between"
                >
                  {content}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-[#E5E7EB] bg-[#F7F8FA]" data-testid="company-info-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-4">
              <div className="mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                {t(ui.contact.companyEyebrow)}
              </div>
              <div className="mt-4 w-8 h-px bg-[#0B1F3A]" />
              <h2 className="mt-6 text-2xl md:text-3xl font-semibold text-[#0B1F3A] tracking-tight">
                {t(ui.contact.companyTitle)}
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6">
              <dl className="border-t border-[#E5E7EB]">
                <div className="grid grid-cols-3 gap-4 py-4 border-b border-[#E5E7EB]">
                  <dt className="col-span-1 mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF] pt-1">
                    {t(ui.contact.fields.name)}
                  </dt>
                  <dd className="col-span-2 text-[#0B1F3A]">Az.r.Oil</dd>
                </div>
                <div className="grid grid-cols-3 gap-4 py-4 border-b border-[#E5E7EB]">
                  <dt className="col-span-1 mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF] pt-1">
                    {t(ui.contact.fields.activity)}
                  </dt>
                  <dd className="col-span-2 text-[#0B1F3A]">{t(ui.contact.fields.activityValue)}</dd>
                </div>
                <div className="grid grid-cols-3 gap-4 py-4 border-b border-[#E5E7EB]">
                  <dt className="col-span-1 mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF] pt-1">
                    {t(ui.contact.fields.segment)}
                  </dt>
                  <dd className="col-span-2 text-[#0B1F3A]">{t(ui.contact.fields.segmentValue)}</dd>
                </div>
                <div className="grid grid-cols-3 gap-4 py-4 border-b border-[#E5E7EB]">
                  <dt className="col-span-1 mono text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF] pt-1">
                    {t(ui.contact.fields.location)}
                  </dt>
                  <dd className="col-span-2 text-[#0B1F3A]">{t(ui.contact.fields.locationValue)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
